/**
 * The payload renderer.
 *
 * A record surface's payload is whatever its handler selected: a row here, a
 * list of related rows there, a few scalars. This component reads that shape
 * and renders it, so a surface with no hand-built screen shows its real data
 * with its real labels rather than a blank page or a stub.
 *
 * It exists for two reasons, and the second one matters more than the first.
 *
 * The first is coverage: there are two dozen bespoke record handlers, and a
 * React port that hand-built each one before any of them worked would have
 * nothing to show for a long time. Every surface renders from day one, and the
 * hand-built screens replace this one at their own pace.
 *
 * The second is that it tells the truth about what it is doing. Where it renders
 * a table it says the columns came from the payload's own keys; where it renders
 * a scalar it labels it with the key. A generic renderer pretending to be a
 * designed screen would be the worst of both, because nobody would know which
 * surfaces still needed work. The banner names it, and the port inventory in
 * the handover guide lists which surfaces are hand built and which are not.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import type { Dict, SectionMeta } from '../api/types'
import { useSession } from '../app/session'
import { appPath } from '../app/paths'
import { Badge, KV, toneOf } from '../components/bits'
import { Panel } from '../components/Panel'

const HIDE = new Set(['section', 'grant', 'audit_link', 'title', 'extra_panels'])

/** A key that reads as money, a date, a percentage or a flag, by its name.
 *  The payload carries no type information per field, so the name is the only
 *  signal available; where it is wrong the figure is still shown, just
 *  punctuated as a plain number rather than as currency. */
function kindOf(key: string, value: unknown): string {
  const k = key.toLowerCase()
  if (typeof value === 'boolean') return 'bool'
  if (value === null || value === undefined) return 'null'
  if (typeof value === 'number') {
    if (/_pct$|percent|_rate$/.test(k)) return 'pct'
    if (/amount|volume|value_sar|_sar$|price|cost|fee|revenue|limit|capital|outstanding/.test(k)) return 'money'
    if (/_ms$/.test(k)) return 'ms'
    if (/confidence/.test(k)) return 'conf'
    return 'int'
  }
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return 'datetime'
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'date'
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(value)) return 'uuid'
    if (/_(status|state|outcome|type|code|reason|level|phase|tone)$/.test(k)) return 'badge'
  }
  return 'text'
}

function Value({ k, v }: { k: string; v: unknown }) {
  const { amount, n, d, dt, lbl } = useSession()
  const kind = kindOf(k, v)
  switch (kind) {
    case 'null': return <span className="muted">—</span>
    case 'bool': return <Badge tone={v ? 'good' : 'muted'}>{v ? 'Yes' : 'No'}</Badge>
    case 'money': return <span className="num">{amount(v)}</span>
    case 'pct': return <span className="num">{n(v, 1)}%</span>
    case 'ms': return <span className="num">{n(v)} ms</span>
    case 'conf': return <span className="num">{n(v, 3)}</span>
    case 'int': return <span className="num">{n(v)}</span>
    case 'date': return <span>{d(v)}</span>
    case 'datetime': return <span>{dt(v)}</span>
    case 'uuid': return <span className="mono clip">{String(v).slice(0, 8)}</span>
    case 'badge': return <Badge tone={toneOf(v)}>{lbl(v)}</Badge>
    default:
      if (typeof v === 'object') {
        return <code className="clip">{JSON.stringify(v).slice(0, 160)}</code>
      }
      return <span className="clip" title={String(v)}>{String(v)}</span>
  }
}

/** A list of objects as a table, with the keys as headings. */
function AutoTable({ rows }: { rows: Dict[] }) {
  const { lbl } = useSession()
  const columns = Array.from(
    rows.slice(0, 8).reduce<Set<string>>((set, row) => {
      for (const key of Object.keys(row)) {
        // Identifiers and long text are dropped from the table and stay
        // available on the record itself: a column of uuids pushes the columns
        // that mean something off the side of the screen.
        if (!/_id$|_pk$|^body$|snapshot|^raw$/.test(key)) set.add(key)
      }
      return set
    }, new Set<string>()),
  ).slice(0, 9)

  return (
    <div className="tbl-wrap">
      <table className="dl">
        <thead>
          <tr>{columns.map((c) => (
            <th key={c} className={/amount|_pct$|count|^n$|_ms$/.test(c) ? 'num' : undefined}>
              {lbl(c)}
            </th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.slice(0, 200).map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c} data-label={lbl(c)}
                    className={/amount|_pct$|count|^n$|_ms$/.test(c) ? 'num' : undefined}>
                  <Value k={c} v={row[c]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 200 && (
        <p className="xs muted">
          {rows.length - 200} further rows are in the payload and not drawn here.
        </p>
      )}
    </div>
  )
}

function Scalars({ entries }: { entries: [string, unknown][] }) {
  const { lbl } = useSession()
  return (
    <KV rows={entries.map(([k, v]) => [lbl(k), <Value k={k} v={v} key={k} />])} />
  )
}

/**
 * Render whatever the handler sent.
 *
 * `sectionKey` is what every panel's information button opens. A panel with no
 * explainer of its own inherits the surface's, which is the honest answer here:
 * the panel was not designed, so it has no logic of its own to explain beyond
 * the surface it belongs to.
 */
export function Introspect({ data, sectionKey, note }: {
  data: Dict
  sectionKey: string
  note?: ReactNode
}) {
  const { lbl } = useSession()
  const entries = Object.entries(data).filter(([k]) => !HIDE.has(k))

  const scalars: [string, unknown][] = []
  const tables: [string, Dict[]][] = []
  const maps: [string, Dict][] = []
  const lists: [string, unknown[]][] = []

  for (const [key, value] of entries) {
    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        tables.push([key, value as Dict[]])
      } else if (value.length > 0) {
        lists.push([key, value])
      }
    } else if (value && typeof value === 'object') {
      maps.push([key, value as Dict])
    } else {
      scalars.push([key, value])
    }
  }

  return (
    <>
      {note}
      {scalars.length > 0 && (
        <Panel title="This record" explain={sectionKey}>
          <Scalars entries={scalars} />
        </Panel>
      )}

      {maps.map(([key, value]) => {
        const inner = Object.entries(value)
        const nestedRows = inner.filter(([, v]) => v && typeof v === 'object'
                                                   && !Array.isArray(v))
        // A map of maps is a keyed collection, such as attributes by key, and
        // reads as a table rather than as a definition list.
        if (nestedRows.length > 2 && nestedRows.length === inner.length) {
          const rows = inner.map(([k, v]) => ({ key: k, ...(v as Dict) }))
          return (
            <Panel title={lbl(key)} explain={sectionKey} key={key} tight>
              <AutoTable rows={rows} />
            </Panel>
          )
        }
        return (
          <Panel title={lbl(key)} explain={sectionKey} key={key}>
            <Scalars entries={inner.filter(([, v]) => !v || typeof v !== 'object')} />
            {inner.filter(([, v]) => Array.isArray(v) && (v as unknown[]).length > 0)
              .map(([k, v]) => (
                <div key={k}>
                  <h4>{lbl(k)}</h4>
                  {typeof (v as unknown[])[0] === 'object'
                    ? <AutoTable rows={v as Dict[]} />
                    : <div className="srcs">
                        {(v as unknown[]).map((item, i) => (
                          <code key={i}>{String(item)}</code>
                        ))}
                      </div>}
                </div>
              ))}
          </Panel>
        )
      })}

      {tables.map(([key, rows]) => (
        <Panel title={`${lbl(key)} · ${rows.length}`} explain={sectionKey}
               key={key} tight>
          <AutoTable rows={rows} />
        </Panel>
      ))}

      {lists.length > 0 && (
        <Panel title="Referenced" explain={sectionKey}>
          {lists.map(([key, items]) => (
            <div key={key}>
              <h4>{lbl(key)}</h4>
              <div className="srcs">
                {items.map((item, i) => <code key={i}>{String(item)}</code>)}
              </div>
            </div>
          ))}
        </Panel>
      )}
    </>
  )
}

/** The banner that says this surface is rendered from its payload. */
export function AutoNote({ section }: { section?: SectionMeta | null }) {
  return (
    <div className="note info">
      <b>Rendered from the payload.</b> This surface has no hand-built React
      screen yet, so its panels, labels and tables are derived from the data the
      server sent. Everything shown is real: the same handler, the same
      permission checks, the same redaction. What is missing is the layout a
      designed screen would give it.
      {section?.section_key && (
        <> The payload's shape is documented under <code>{section.section_key}</code>{' '}
        in the port inventory.</>
      )}
    </div>
  )
}

export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return <Link className="btn sm" to={appPath(to)}>{children}</Link>
}
